import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import { endpoint } from "../../utils/endpoints";

const ACCOUNTANT_API = process.env.REACT_APP_ACCOUNTANT_API;

const C = {
  border: "#E5E7EB",
  text: "#111827",
  muted: "#6B7280",
  primary: "#000053",
  tableHead: "#000053",
};

const parseBody = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { rawMessage: text };
  }
};

const getErrorMessage = (payload, response) => {
  if (!payload || typeof payload !== "object") {
    return response.statusText || "Something went wrong. Please try again.";
  }
  return (
    payload.errorMessage ||
    payload.message ||
    payload.error ||
    (Array.isArray(payload.errors) ? payload.errors.join(", ") : null) ||
    `Request failed (${response.status})`
  );
};

const parseNotificationList = (payload) =>
  Array.isArray(payload?.data) ? payload.data : [];

const mapNotification = (item, index) => ({
  id: item?._id ?? `notification-${index}`,
  message: item?.message || "—",
});

const Notifications = () => {
  const navigate = useNavigate();
  const { setSubheading } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [markingRead, setMarkingRead] = useState(false);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    setSubheading("Notifications");
    return () => setSubheading("");
  }, [setSubheading]);

  const fetchNotifications = useCallback(async () => {
    const token = localStorage.getItem("sessionToken");
    if (!token) {
      toast.error("Session expired. Please log in again.");
      setNotifications([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${ACCOUNTANT_API}${endpoint.listOfNotification}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await parseBody(response);
      if (!response.ok || payload?.code !== 200) {
        toast.error(getErrorMessage(payload, response));
        setNotifications([]);
        return;
      }
      const list = parseNotificationList(payload);
      setNotifications(list.map(mapNotification));
    } catch (err) {
      console.error(err);
      toast.error(
        err?.message === "Failed to fetch"
          ? "Network error. Check your connection."
          : "Could not load notifications."
      );
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markNotificationsRead = useCallback(async () => {
    const token = localStorage.getItem("sessionToken");
    if (!token) return false;

    try {
      const response = await fetch(`${ACCOUNTANT_API}${endpoint.readAllNotification}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const payload = await parseBody(response);
      return response.ok && payload?.code === 200;
    } catch (err) {
      console.error(err);
      return false;
    }
  }, []);

  const handleNotificationClick = useCallback(async () => {
    if (markingRead) return;
    setMarkingRead(true);
    try {
      const ok = await markNotificationsRead();
      if (!ok) {
        toast.error("Could not mark notifications as read.");
        return;
      }
      setNotifications((prev) => [...prev]);
    } finally {
      setMarkingRead(false);
    }
  }, [markNotificationsRead, markingRead]);

  return (
    <div className="tab-pane-content mt-7 font-UbuntuRegular">
      <div className="mb-4 flex justify-start">
        <button type="button" className="btn btn-primary" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: C.border }}>
        <div className="px-4 py-4 sm:px-6">
          <div className="rounded-lg border bg-white overflow-hidden" style={{ borderColor: C.border }}>
            <table className="w-full mb-0 bg-white">
              <colgroup>
                <col style={{ width: "88px" }} />
                <col />
              </colgroup>
              <thead>
                <tr className="text-center" style={{ background: C.tableHead }}>
                  {["S.No.", "Message"].map((title) => (
                    <th
                      key={title}
                      className="text-[13px] font-UbuntuMedium text-white whitespace-nowrap px-3 py-3.5 border-0"
                    >
                      {title}
                    </th>
                  ))}
                </tr>
              </thead>
            </table>
            <div className="notification-body-scroll max-h-[60vh] overflow-y-auto border-t" style={{ borderColor: C.border }}>
              <table className="w-full mb-0 bg-white">
                <colgroup>
                  <col style={{ width: "88px" }} />
                  <col />
                </colgroup>
                <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={2} className="text-center py-10" style={{ color: C.muted }}>
                      Loading notifications…
                    </td>
                  </tr>
                ) : notifications.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="text-center py-10" style={{ color: C.muted }}>
                      No notifications found.
                    </td>
                  </tr>
                ) : (
                  notifications.map((item, i) => (
                    <tr
                      key={item.id}
                      onClick={handleNotificationClick}
                      className={`text-center ${i % 2 ? "bg-[#FAFAFA]" : "bg-white"} ${
                        markingRead ? "cursor-not-allowed" : "cursor-pointer"
                      }`}
                    >
                      <td className="px-3 py-4">{String(i + 1).padStart(2, "0")}</td>
                      <td className="px-3 py-4 text-center">{item.message}</td>
                    </tr>
                  ))
                )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notifications;
